import { useState } from 'react';
import { Typography, Row, Col, Modal, Form, Input, message } from 'antd';
import { InputMask } from '@components/atoms';
import { api } from '@common/api';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface ContactFormValues {
  name: string;
  email: string;
  phone: string;
  message: string;
}

interface ContactFormProps {
  onFinish: (values: ContactFormValues) => void;
  isSubmitting: boolean;
}

// Contact Form Component
const ContactForm = ({ onFinish, isSubmitting }: ContactFormProps) => {
  return (
    <Form
      name="contact-form-modal"
      layout="vertical"
      onFinish={onFinish}
      requiredMark={false}
      disabled={isSubmitting}
    >
      <Form.Item
        label={<span className="font-orator text-white">FULL NAME</span>}
        name="name"
        rules={[{ required: true, message: 'please enter your name' }]}
      >
        <Input className="bg-zinc-800 font-orator border-zinc-700 text-white" placeholder="Your full name" />
      </Form.Item>
      <Form.Item
        label={<span className="font-orator text-white">EMAIL ADDRESS</span>}
        name="email"
        rules={[{ required: true, message: 'please enter your email' }, { type: 'email' }]}
      >
        <Input className="bg-zinc-800 font-orator border-zinc-700 text-white" placeholder="Your email address" />
      </Form.Item>
      <Form.Item
        label={<span className="font-orator text-white">PHONE NUMBER</span>}
        name="phone"
        rules={[
          { required: true, message: 'please enter your phone number' },
          {
            pattern: /^(\+1\s?)?(\(\d{3}\)|\d{3})[\s.-]?\d{3}[\s.-]?\d{4}$/,
            message: 'please enter a valid US phone number',
          },
        ]}
      >
        <InputMask 
          mask="usphone"
          className="bg-zinc-800 font-orator border-zinc-700 text-white" 
          placeholder="(555) 123-4567"
        />
      </Form.Item>
      <Form.Item
        label={<span className="font-orator text-white">MESSAGE</span>}
        name="message"
        rules={[{ required: true, message: 'please enter your message' }]}
      >
        <TextArea rows={4} className="bg-zinc-800 font-orator text-white" placeholder="Your message" />
      </Form.Item>
      <div className="text-center pt-4">
        <button type="submit" className="btn-orangicide font-displayed !font-normal text-xl tracking-wider w-55 h-10 cursor-pointer" disabled={isSubmitting}>
          {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
        </button>
      </div>
    </Form>
  );
};

export const FabricationPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showModal = () => setIsModalVisible(true);
  const handleCancel = () => setIsModalVisible(false);

  const onFormFinish = async (values: ContactFormValues) => {
    setIsSubmitting(true);
    try {
      await api.post('/contact', values);
      setIsModalVisible(false);
      message.success('Your request has been sent successfully!');
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Unable to send your message. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      {/* Hero Section */}
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat relative w-full h-[400px]">
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 z-10">
          <div className="font-displayed !font-normal text-5xl lg:text-6xl text-white tracking-wider text-center">
            FABRICATION
          </div>
        </div>
      </section>
      
      {/* Company Story */}
      <section className="w-full bg-first-dark-gray pt-8 pb-18">
        <div className="container mx-auto px-4">
          <Row gutter={[48, 48]} align="middle">
            <Col md={24} lg={24}>
              <Title level={1} className="font-displayed !font-normal text-orangicide mt-4">PRECISION FABRICATION</Title>
              <Paragraph className="font-orator text-white text-justify tracking-wider pb-4">
                AT MOD-LINK, FABRICATION ISN'T JUST A SERVICE—IT'S A CORE COMPETENCY. OUR IN-HOUSE FABRICATION FACILITY IN TRUCKEE, CALIFORNIA, IS WHERE IDEAS BECOME REALITY. WE DESIGN AND MANUFACTURE HEAVY-DUTY COMPONENTS THAT MEET THE EXTREME DEMANDS OF SNOW REMOVAL AND TRACTION OPERATIONS. FROM HOOKS AND CONNECTING LINKS TO CUSTOM HARDWARE SOLUTIONS, EVERY PIECE IS ENGINEERED FOR STRENGTH, DURABILITY, AND SEAMLESS INTEGRATION WITH OUR CHAIN SYSTEMS.
              </Paragraph>
              <Paragraph className="font-orator text-white text-justify tracking-wider pb-4">
                OUR FABRICATION PROCESS COMBINES TRADITIONAL CRAFTSMANSHIP WITH MODERN TECHNOLOGY. WE USE HIGH-GRADE MATERIALS AND PRECISION EQUIPMENT TO PRODUCE COMPONENTS THAT CAN WITHSTAND THE HARSHEST CONDITIONS. EACH PIECE UNDERGOES RIGOROUS QUALITY CONTROL TO ENSURE IT MEETS OUR EXACTING STANDARDS. WHETHER IT'S A STANDARD COMPONENT OR A CUSTOM SOLUTION, WE BRING THE SAME LEVEL OF ATTENTION TO DETAIL AND COMMITMENT TO EXCELLENCE.
              </Paragraph>
              <Paragraph className="font-orator text-white text-justify tracking-wider">
                WHAT SETS MOD-LINK APART IS OUR ABILITY TO INNOVATE RAPIDLY. BECAUSE WE CONTROL THE ENTIRE FABRICATION PROCESS, WE CAN QUICKLY PROTOTYPE NEW DESIGNS, TEST THEM IN REAL-WORLD CONDITIONS, AND REFINE THEM BASED ON FEEDBACK FROM OPERATORS IN THE FIELD. THIS AGILITY ALLOWS US TO STAY AHEAD OF INDUSTRY NEEDS AND CONTINUOUSLY IMPROVE OUR PRODUCTS. WE DON'T JUST MANUFACTURE COMPONENTS—WE CREATE SOLUTIONS THAT SOLVE REAL PROBLEMS FOR REAL PEOPLE.
              </Paragraph>
            </Col>
          </Row>
        </div>
      </section>

      {/* Process Section */}
      <section>
        <div className="container w-full max-w-8xl mx-auto px-6 md:pb-8">
          <div className="font-displayed !font-normal text-white text-5xl tracking-wider pt-12 !mb-8">
            OUR PROCESS
          </div>

          {/* Design and Engineering */}
          <div className="mb-20">
            <Title level={1} className="font-displayed !font-normal text-orangicide tracking-wider">
              Design and engineering
            </Title>
            <Paragraph className="font-orator text-white !text-lg text-justify tracking-wider leading-8">
              Every component begins with careful design and engineering. Our team evaluates the specific requirements and performance expectations to create optimal solutions. We use 3D modelling software and 3D printing to rapidly prototype ideas, test concepts, and refine designs before fabrication begins. Whether we're adapting an existing design or creating something entirely new, precision and functionality guide every decision.
            </Paragraph>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat pb-12">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="font-displayed !font-normal text-white text-4xl md:text-5xl uppercase mb-6">
            NEED CUSTOM FABRICATION?
          </div>
          <Paragraph className="font-orator text-white !text-xl max-w-3xl mx-auto mb-8">
            Whether you need standard components or custom solutions, our fabrication team is ready to help. Contact us to discuss your requirements and discover how Mod-Link can support your operations.
          </Paragraph>
          <button 
            onClick={showModal}
            className="btn-orangicide font-displayed !font-normal text-2xl w-full max-w-xs sm:w-80 py-4 tracking-wider cursor-pointer"
          >
            CONTACT US
          </button>
        </div>
      </section>

      {/* Modal with Contact Form */}
      <Modal
        title={<Title level={3} className="font-orator font-bold text-orangicide">REQUEST INFORMATION</Title>}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        closeIcon={<span className="text-orangicide">X</span>}
        centered
        styles={{
          mask: { backdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.7)'},
          content: { backgroundColor: '#181717', border: '1px solid #3f3f46', borderRadius: '0px', width: 'auto', maxWidth: '500px' },
          header: { backgroundColor: '#181717', borderBottom: '1px solid #3f3f46' },
        }}
      >
        <ContactForm onFinish={onFormFinish} isSubmitting={isSubmitting} />
      </Modal>
    </>
  );
}; 

